"use client"

import { useState } from "react"
import { Box, Button, Flex, Text, Tooltip, Icon, useToast } from "@chakra-ui/react"
import { FaUserSecret, FaSignOutAlt } from "react-icons/fa"
import { useAuthentication } from "../wrappers/UseAuthentication"

const TYPE_LABELS = { admin: "Master", CAM: "CAM", org_admin: "Org admin", supplier: "Supplier", SME: "Basic" }

/**
 * Teleport out (2026-09-17, docs/michael/CHANGES-2026-09-17-teleport.md).
 *
 * The floating purple badge a MASTER sees while signed in as another account.
 * Renders only when AuthProvider holds a live teleport — which only a
 * successful /admin/teleport-in sets and which a page load clears — so a normal
 * user can never see it. Purple on purpose: not part of the app's palette, so
 * it reads as "this is not the product, this is you looking through someone's
 * eyes".
 */
const TeleportBadge = () => {
  const { teleport, teleportOut } = useAuthentication()
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  if (!teleport) return null

  const name = teleport.target?.fname || teleport.target?.username || "user"
  const type = TYPE_LABELS[teleport.target?.user_type] || teleport.target?.user_type || ""

  const out = async () => {
    setBusy(true)
    try {
      await teleportOut()
      toast({ title: "Teleported out", description: `Back in your own account (${teleport.from?.fname || "master"}).`, status: "info", duration: 3000, isClosable: true })
    } catch (e) {
      toast({ title: "Teleport out failed", description: e?.message || "Reload the page — that also returns you to your account.", status: "error", duration: 6000, isClosable: true })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Tooltip
      label="You are signed in as this user. Everything you see is theirs, and anything you change is recorded as theirs. Reloading the page also ends the teleport."
      placement="bottom-end"
      openDelay={300}
    >
      <Flex
        role="status"
        aria-label={`Teleported into ${name}`}
        data-testid="teleport-badge"
        position="fixed"
        top="10px"
        right="14px"
        zIndex={2000}
        align="center"
        gap={2}
        bg="purple.600"
        color="white"
        px={3}
        py={1.5}
        borderRadius="full"
        boxShadow="0 4px 14px rgba(76, 29, 149, 0.45)"
        border="1px solid"
        borderColor="purple.300"
      >
        <Icon as={FaUserSecret} boxSize="14px" />
        <Box lineHeight="1.1">
          <Text fontSize="10px" letterSpacing="wide" textTransform="uppercase" opacity={0.85}>
            Teleported in
          </Text>
          <Text fontSize="sm" fontWeight="600" whiteSpace="nowrap" maxW="240px" overflow="hidden" textOverflow="ellipsis">
            {name}
            {type ? ` · ${type}` : ""}
          </Text>
        </Box>
        <Button
          size="xs"
          ml={1}
          bg="white"
          color="purple.700"
          _hover={{ bg: "purple.50" }}
          leftIcon={<FaSignOutAlt />}
          onClick={out}
          isLoading={busy}
          loadingText="Leaving"
        >
          Teleport out
        </Button>
      </Flex>
    </Tooltip>
  )
}

export default TeleportBadge
